import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Controller, useFieldArray, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as Yup from "yup";
import {
  Button,
  IconButton,
  InlineLoading,
  RadioButton,
  RadioButtonGroup,
  Toggle,
} from "@carbon/react";
import { Add, TrashCan } from "@carbon/icons-react";
import Form from "../../../components/carbon/Form/Form";
import SelectField from "../../../components/carbon/Form/SelectField";
import TextField from "../../../components/carbon/Form/TextField";
import PageHeader from "../../../components/carbon/PageHeader/PageHeader";
import { useNotification } from "../../../context";
import { useFamilyOptions } from "../../../hooks/useCategories";
import {
  useCategoryVat,
  useOcSettings,
  useReplaceCategoryVat,
  useUpdateOcSettings,
} from "../../../hooks/useOcSettings";
import { CategoryVat, OcSettings, OcSettingsUpdate } from "../../../types";
import HolidayCalendar from "./HolidayCalendar";
import "./ocSettings.scss";

/** One per-category VAT override row. `rate` stays a string while typing. */
interface VatRow {
  categoryId: string;
  rate: string;
}

interface OcSettingsFormFields {
  autoApproval: boolean;
  tacitEnabled: boolean;
  /** "net" = the approval threshold compares the net total; "gross" = the total with VAT. */
  thresholdBase: "net" | "gross";
  /** Percent; `""` = not configured. */
  defaultVatRate: string;
  categoryVat: VatRow[];
}

const emptyVatRow: VatRow = { categoryId: "", rate: "" };

const toFormFields = (
  settings: OcSettings,
  categoryVat: CategoryVat[]
): OcSettingsFormFields => ({
  autoApproval: settings.autoApproval,
  tacitEnabled: settings.tacitEnabled,
  thresholdBase: settings.thresholdIncludesTax ? "gross" : "net",
  defaultVatRate:
    settings.defaultVatRate === null || settings.defaultVatRate === undefined
      ? ""
      : String(settings.defaultVatRate),
  categoryVat: categoryVat.map((row) => ({
    categoryId: row.categoryId,
    rate: String(row.rate),
  })),
});

/**
 * The PUT of `/purchase-orders/settings` is PARTIAL (null / omitted = unchanged), so only the
 * fields that differ from what was loaded are sent: a stale form can never clobber a value the
 * user did not touch. `defaultVatRate` cannot be cleared through this endpoint, which is why the
 * schema refuses a blank once a rate has been configured.
 */
const settingsChanges = (
  values: OcSettingsFormFields,
  baseline: OcSettingsFormFields
): OcSettingsUpdate => {
  const changes: OcSettingsUpdate = {};
  if (values.autoApproval !== baseline.autoApproval) {
    changes.autoApproval = values.autoApproval;
  }
  if (values.tacitEnabled !== baseline.tacitEnabled) {
    changes.tacitEnabled = values.tacitEnabled;
  }
  if (values.thresholdBase !== baseline.thresholdBase) {
    changes.thresholdIncludesTax = values.thresholdBase === "gross";
  }
  if (
    values.defaultVatRate.trim() !== "" &&
    (baseline.defaultVatRate === "" ||
      Number(values.defaultVatRate) !== Number(baseline.defaultVatRate))
  ) {
    changes.defaultVatRate = Number(values.defaultVatRate);
  }
  return changes;
};

const toCategoryVatRequest = (rows: VatRow[]): CategoryVat[] =>
  rows.map((row) => ({ categoryId: row.categoryId, rate: Number(row.rate) }));

const isPercent = (value?: string) =>
  value !== undefined &&
  value.trim() !== "" &&
  Number.isFinite(Number(value)) &&
  Number(value) >= 0 &&
  Number(value) <= 100;

const buildSchema = (t: (key: string) => string, vatConfigured: boolean) =>
  Yup.object({
    autoApproval: Yup.boolean().default(false),
    tacitEnabled: Yup.boolean().default(false),
    thresholdBase: Yup.string().oneOf(["net", "gross"]).default("net"),
    defaultVatRate: Yup.string()
      .default("")
      .test(
        "default-vat",
        t("purchaseOrders.settings.validation.rate"),
        (value?: string) =>
          (value ?? "").trim() === "" ? !vatConfigured : isPercent(value)
      ),
    categoryVat: Yup.array()
      .of(
        Yup.object({
          categoryId: Yup.string().required(
            t("purchaseOrders.settings.validation.category")
          ),
          rate: Yup.string()
            .required(t("purchaseOrders.settings.validation.rate"))
            .test(
              "rate",
              t("purchaseOrders.settings.validation.rate"),
              isPercent
            ),
        })
      )
      .default([])
      .test(
        "unique-categories",
        t("purchaseOrders.settings.validation.duplicateCategory"),
        (rows?: { categoryId?: string }[]) => {
          const ids = (rows ?? []).map((row) => row?.categoryId);
          return new Set(ids).size === ids.length;
        }
      ),
  });

const errorMessage = (error: unknown): string | undefined =>
  (error as { message?: string; root?: { message?: string } } | undefined)?.root
    ?.message ?? (error as { message?: string } | undefined)?.message;

const statusOf = (error: unknown) =>
  (error as { response?: { status?: number } } | null)?.response?.status;

/**
 * Company-level settings of the purchase-order (OC) module: auto-approval, whether the approval
 * threshold compares the net or the gross total, the default VAT rate, per-category VAT overrides
 * and tacit acceptance. Save stays disabled until something differs from what was loaded / last
 * saved. It writes to two endpoints — the partial settings PUT (only changed fields) and the
 * REPLACE category-VAT PUT (the full desired set) — each only when its part changed. A 400 (unknown
 * / duplicate category, rate out of range) is an error toast, never a crash.
 */
const OcSettingsForm = ({ baseline }: { baseline: OcSettingsFormFields }) => {
  const { t } = useTranslation();
  const { openNotification } = useNotification();
  const updateSettings = useUpdateOcSettings();
  const replaceVat = useReplaceCategoryVat();
  const families = useFamilyOptions();
  const [saving, setSaving] = useState(false);

  const schema = useMemo(
    () => buildSchema(t, baseline.defaultVatRate !== ""),
    [t, baseline]
  );

  const {
    control,
    handleSubmit,
    reset,
    watch,
    formState: { isDirty, errors },
  } = useForm<OcSettingsFormFields>({
    resolver: yupResolver(schema) as never,
    defaultValues: baseline,
  });

  // A (re)fetched settings payload is the new baseline.
  useEffect(() => {
    reset(baseline);
  }, [baseline, reset]);

  const { fields, append, remove } = useFieldArray({
    control,
    name: "categoryVat",
  });

  const familyOptions = (index: number) => {
    const options = [...families];
    const current = watch(`categoryVat.${index}.categoryId`);
    if (current && !options.some((o) => o.value === current)) {
      options.push({
        value: current,
        label: t("purchaseOrders.settings.unknownCategory", { id: current }),
      });
    }
    return options;
  };

  const onSubmit = async (values: OcSettingsFormFields) => {
    const changes = settingsChanges(values, baseline);
    const vatChanged =
      JSON.stringify(values.categoryVat) !==
      JSON.stringify(baseline.categoryVat);
    setSaving(true);
    try {
      if (Object.keys(changes).length > 0) {
        await updateSettings.mutateAsync(changes);
      }
      if (vatChanged) {
        await replaceVat.mutateAsync(toCategoryVatRequest(values.categoryVat));
      }
      reset(values);
      openNotification(t("purchaseOrders.settings.success"));
    } catch (error) {
      openNotification(
        t(
          statusOf(error) === 400
            ? "purchaseOrders.settings.invalid"
            : "purchaseOrders.settings.error"
        )
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <Form
      handleSubmit={handleSubmit}
      onSubmit={onSubmit}
      noValidate
      className="oc-settings__form"
    >
      <section className="oc-settings__section">
        <h2 className="oc-settings__heading">
          {t("purchaseOrders.settings.approvalSection")}
        </h2>
        <Controller
          name="autoApproval"
          control={control}
          render={({ field }) => (
            <Toggle
              id="autoApproval"
              labelText={t("purchaseOrders.settings.autoApproval")}
              labelA={t("purchaseOrders.settings.off")}
              labelB={t("purchaseOrders.settings.on")}
              toggled={field.value}
              onToggle={(checked: boolean) => field.onChange(checked)}
            />
          )}
        />
        <p className="oc-settings__help">
          {t("purchaseOrders.settings.autoApprovalHelp")}
        </p>
        <Controller
          name="thresholdBase"
          control={control}
          render={({ field }) => (
            <RadioButtonGroup
              name="thresholdBase"
              legendText={t("purchaseOrders.settings.thresholdBase")}
              helperText={t("purchaseOrders.settings.thresholdBaseHelp")}
              valueSelected={field.value}
              onChange={(value: string | number | undefined) =>
                field.onChange(value)
              }
              orientation="horizontal"
            >
              <RadioButton
                id="thresholdBase-net"
                value="net"
                labelText={t("purchaseOrders.settings.net")}
              />
              <RadioButton
                id="thresholdBase-gross"
                value="gross"
                labelText={t("purchaseOrders.settings.gross")}
              />
            </RadioButtonGroup>
          )}
        />
      </section>

      <section className="oc-settings__section">
        <h2 className="oc-settings__heading">
          {t("purchaseOrders.settings.tacitSection")}
        </h2>
        <Controller
          name="tacitEnabled"
          control={control}
          render={({ field }) => (
            <Toggle
              id="tacitEnabled"
              labelText={t("purchaseOrders.settings.tacitEnabled")}
              labelA={t("purchaseOrders.settings.off")}
              labelB={t("purchaseOrders.settings.on")}
              toggled={field.value}
              onToggle={(checked: boolean) => field.onChange(checked)}
            />
          )}
        />
        <p className="oc-settings__help">
          {t("purchaseOrders.settings.tacitHelp")}
        </p>
      </section>

      <section className="oc-settings__section">
        <h2 className="oc-settings__heading">
          {t("purchaseOrders.settings.vatSection")}
        </h2>
        <div className="oc-settings__default-vat">
          <TextField
            name="defaultVatRate"
            label={t("purchaseOrders.settings.defaultVatRate")}
            control={control}
            type="number"
            min={0}
            max={100}
            step="any"
            helperText={t("purchaseOrders.settings.defaultVatRateHelp")}
          />
        </div>

        <div className="oc-settings__vat-header">
          <h3 className="oc-settings__subheading">
            {t("purchaseOrders.settings.categoryVat")}
          </h3>
          <Button
            kind="tertiary"
            size="md"
            renderIcon={Add}
            type="button"
            onClick={() => append(emptyVatRow)}
          >
            {t("purchaseOrders.settings.addCategoryVat")}
          </Button>
        </div>
        <p className="oc-settings__help">
          {t("purchaseOrders.settings.categoryVatHelp")}
        </p>
        {errorMessage(errors.categoryVat) ? (
          <p role="alert" className="oc-settings__error">
            {errorMessage(errors.categoryVat)}
          </p>
        ) : null}
        {fields.length === 0 ? (
          <p className="oc-settings__empty">
            {t("purchaseOrders.settings.categoryVatEmpty")}
          </p>
        ) : (
          <div className="oc-settings__rows">
            {fields.map((field, index) => (
              <div
                key={field.id}
                className="oc-settings__row"
                role="group"
                aria-label={t("purchaseOrders.settings.categoryVatRow", {
                  index: index + 1,
                })}
              >
                <div className="oc-settings__grid">
                  <SelectField
                    name={`categoryVat.${index}.categoryId`}
                    label={t("purchaseOrders.settings.category")}
                    control={control}
                    placeholder={t("purchaseOrders.settings.chooseCategory")}
                    options={familyOptions(index)}
                  />
                  <TextField
                    name={`categoryVat.${index}.rate`}
                    label={t("purchaseOrders.settings.rate")}
                    control={control}
                    type="number"
                    min={0}
                    max={100}
                    step="any"
                  />
                </div>
                <IconButton
                  kind="ghost"
                  size="md"
                  label={t("purchaseOrders.settings.removeCategoryVat", {
                    index: index + 1,
                  })}
                  onClick={() => remove(index)}
                >
                  <TrashCan />
                </IconButton>
              </div>
            ))}
          </div>
        )}
      </section>

      <div className="oc-settings__actions">
        <Button type="submit" disabled={saving || !isDirty}>
          {t("purchaseOrders.settings.save")}
        </Button>
      </div>
    </Form>
  );
};

const OcSettingsPanel = () => {
  const { t } = useTranslation();
  const settingsQuery = useOcSettings();
  const vatQuery = useCategoryVat();

  const settings = settingsQuery.data;
  const categoryVat = vatQuery.data;
  const baseline = useMemo(
    () => (settings ? toFormFields(settings, categoryVat ?? []) : undefined),
    [settings, categoryVat]
  );
  const isLoading = settingsQuery.isLoading || vatQuery.isLoading;
  const isError = settingsQuery.isError || vatQuery.isError;

  return (
    <div className="oc-settings">
      <PageHeader title={t("purchaseOrders.settings.title")} />
      <p className="oc-settings__description">
        {t("purchaseOrders.settings.description")}
      </p>

      {isLoading ? (
        <InlineLoading description={t("cta.loading")} />
      ) : isError || !baseline ? (
        <p role="alert">{t("purchaseOrders.settings.loadError")}</p>
      ) : (
        <OcSettingsForm baseline={baseline} />
      )}

      <HolidayCalendar />
    </div>
  );
};

export default OcSettingsPanel;
